import { useNavigate } from 'react-router-dom';
import { translate } from '../../lib/translations';
import { TeamLogo } from '../TeamLogo';
import { PoolerFollowButton } from '../PoolerFollows/PoolerFollowButton';

export interface StandingsRow {
  user_id: number;
  username: string;
  rank?: number;
  nhl_team_abbrev?: string | null;
  totgoals: number;
  totassists: number;
  totgoals_goalie: number;
  totassists_goalie: number;
  totwins_goalie: number;
  totshutout_goalie: number;
  tototlosses_goalie: number;
}

interface StandingsTableProps {
  standings: StandingsRow[];
  language: 'en' | 'fr';
  currentUserId?: number | null;
  leagueId?: number | null;
  followedUserIds?: Set<number>;
  showFollowButton?: boolean;
  onFollowChanged?: (targetUserId: number, isFollowed: boolean) => void;
  scrollable?: boolean;
}

export function StandingsTable({
  standings,
  language,
  currentUserId,
  leagueId,
  followedUserIds,
  showFollowButton = false,
  onFollowChanged,
  scrollable,
}: StandingsTableProps) {
  const navigate = useNavigate();
  const canShowFollowButton = showFollowButton && !!leagueId && !!currentUserId;

  const handleDGClick = (userId: number) => {
    navigate(`/other-teams/${userId}`);
  };

  return (
    <div>
      <h6 className="mb-5">{translate('standing_note', language)}</h6>{' '}
      {/* Texte avant la grille */}
      <div
        className={`table-responsive${scrollable ? ' standings-scroll' : ''}`}
        data-testid="standings-scroll-container"
      >
        <table className="table table-hover">
          <thead>
            <tr>
              <th>{translate('rank', language)}</th>
              <th>{translate('total.points', language)}</th>
              <th>{translate('team', language)}</th>
              {canShowFollowButton && <th>{language === 'fr' ? 'Suivi' : 'Follow'}</th>}
              <th>{translate('goals', language)}</th>
              <th>{translate('assists', language)}</th>
              <th>{translate('g_wins', language)}</th>
              <th>{translate('shutout', language)}</th>
              <th>{translate('stat.otLosses', language)}</th>
              <th>{translate('goals_goalie', language)}</th>
              <th>{translate('assists_goalie', language)}</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // If backend provides `rank`, rely on it (already ordered). Otherwise compute totals and sort client-side.
              const hasRank = standings.some((s) => typeof s.rank === 'number');
              const rows = standings.slice();
              if (!hasRank) {
                rows.sort((a, b) => {
                  const sumB =
                    b.totgoals +
                    b.totassists +
                    b.totgoals_goalie +
                    b.totassists_goalie +
                    b.totwins_goalie +
                    b.totshutout_goalie +
                    b.tototlosses_goalie;
                  const sumA =
                    a.totgoals +
                    a.totassists +
                    a.totgoals_goalie +
                    a.totassists_goalie +
                    a.totwins_goalie +
                    a.totshutout_goalie +
                    a.tototlosses_goalie;
                  return sumB - sumA;
                });
              } else {
                rows.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
              }

              return rows.map((team, index) => {
                const isMe = currentUserId !== null && currentUserId !== undefined && Number(team.user_id) === Number(currentUserId);
                return (
                <tr
                  key={team.user_id}
                  className={isMe ? 'is-me' : undefined}
                  data-user-id={team.user_id}
                  data-is-me={isMe ? 'true' : undefined}
                >
                  <td>{team.rank ?? index + 1}</td>
                  <td className="fw-bold">
                    {(team.totgoals || 0) +
                      (team.totassists || 0) +
                      (team.totgoals_goalie || 0) +
                      (team.totassists_goalie || 0) +
                      (team.totwins_goalie || 0) +
                      (team.totshutout_goalie || 0) +
                      (team.tototlosses_goalie || 0)}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none p-0 fw-bold text-primary d-flex align-items-center gap-2"
                      onClick={() => handleDGClick(team.user_id)}
                      style={{ cursor: 'pointer' }}
                      title={`Voir l'équipe de ${team.username}`}
                    >
                      {team.nhl_team_abbrev && (
                        <TeamLogo abbrev={team.nhl_team_abbrev} size={28} />
                      )}
                      {team.username}
                    </button>
                  </td>
                  {canShowFollowButton && (
                    <td>
                      {!isMe && (
                        <PoolerFollowButton
                          leagueId={leagueId}
                          currentUserId={currentUserId}
                          targetUserId={team.user_id}
                          initialFollowed={followedUserIds?.has(Number(team.user_id)) ?? false}
                          onChanged={onFollowChanged}
                        />
                      )}
                    </td>
                  )}
                  <td>{team.totgoals}</td>
                  <td>{team.totassists}</td>
                  <td>{team.totwins_goalie}</td>
                  <td>{team.totshutout_goalie}</td>
                  <td>{team.tototlosses_goalie}</td>
                  <td>{team.totgoals_goalie}</td>
                  <td>{team.totassists_goalie}</td>
                </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
